import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  Suspense,
  lazy,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Logo } from './components/Logo';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResetPasswordConfirmPage from './pages/ResetPasswordConfirmPage';
import NotFoundPage from './pages/NotFoundPage';
import Subscription from './components/Subscription';
import SharedItemViewer from './components/SharedItemViewer';
import VideoGenerator from './components/VideoGenerator';
import DocumentGenerator from './components/DocumentGenerator';
import './styles/theme-transitions.css';
import WorkflowTracker, { stages } from './components/WorkflowTracker';
import { ErrorBoundary } from 'react-error-boundary';
import { CircularProgress, Typography } from '@mui/material';
import {
  useThemeMode,
  ThemeProvider as CustomThemeProvider,
} from './contexts/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import {
  BrowserRouter as Router,
  useNavigate,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import Footer from './components/Footer';
import Box from '@mui/material/Box';
import Sidebar from './components/Sidebar';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import OpenIcon from '@mui/icons-material/OpenInNew';
import { Helmet } from 'react-helmet-async';
import { SWRConfig } from 'swr';
import Auth from './components/Auth';
import EditProfile from './components/EditProfile';
import Gallery from './components/Gallery';
import Notifications from './components/Notifications';
// Removed AdminInterface import
import CanvasEditor from './components/CanvasEditor';
import WorkflowDirectory from './components/WorkflowDirectory';
import Onboarding, { FeatureTooltip, TutorialTip } from './components/Onboarding';
import UserDashboard from './components/UserDashboard';
import '@fontsource/inter';
import { supabase } from './supabaseClient';
import { styled } from '@mui/system';

import PropertyGallery from './components/PropertyGallery';
import PropertyDetail from './components/PropertyDetail';
import SharedLinks from './components/SharedLinks';
import ChatTab from './components/ChatTab';
import ImageEditor from './components/ImageEditor';

const SubscriptionWrapper = () => {
  const [session, setSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };
    getSession();
  }, []);

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      px: 0,
      position: 'relative'
    }}>
      {/* Button moved to Subscription.js */}
      <Subscription session={session} />
    </Box>
  );
};

const DraggableNavbar = styled('div')(({ theme }) => ({
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#00ffff rgba(0, 255, 255, 0.1)',
  msOverflowStyle: 'none',
}));

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#004aad',
      light: '#3373c4',
      dark: '#003580',
    },
    secondary: {
      main: '#A2C3A4',
      light: '#cbf199',
      dark: '#869d96',
    },
    background: {
      default: '#fdfdfd',
      paper: '#fdfdfd',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#fdfdfd',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#000000',
          color: '#FFFFFF',
          fontSize: '0.875rem',
          borderRadius: '4px',
          padding: '8px 12px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.MuiButton-contained': {
            backgroundColor: '#004aad',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#003580',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 74, 173, 0.7)', // Light blue for unselected tabs
          '&.Mui-selected': {
            color: '#004aad', // Blue for selected tab
          },
          '&:hover': {
            color: '#004aad', // Blue on hover
            backgroundColor: 'rgba(0, 74, 173, 0.1)', // Slight blue background on hover
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#004aad', // Blue for loading spinner
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#869d96', // Dark green-gray
        },
        bar: {
          backgroundColor: '#004aad', // Blue for progress bar
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#004aad',
      light: '#3373c4',
      dark: '#003580',
    },
    secondary: {
      main: '#A2C3A4',
      light: '#cbf199',
      dark: '#869d96',
    },
    background: {
      default: '#1A1A2A',
      paper: '#242436',
      elevation1: '#2A2A40',
      elevation2: '#32324A',
      elevation3: '#3A3A54',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A2C3A4',
      disabled: '#666670',
    },
    action: {
      active: '#004aad',
      hover: 'rgba(0, 74, 173, 0.12)',
      selected: 'rgba(0, 74, 173, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: '#E0E0E5',
    },
    body1: {
      fontWeight: 400,
      letterSpacing: '0.01em',
      color: '#A0A0B0',
    },
    body2: {
      fontWeight: 400,
      letterSpacing: '0.01em',
      color: '#A0A0B0',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'none',
          backgroundColor: '#0A0A0F',
          scrollbarColor: '#272733 #141420',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#141420',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#272733',
            borderRadius: '4px',
            '&:hover': {
              background: '#373744',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(20, 20, 32, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#141420',
          backgroundImage:
            'linear-gradient(180deg, rgba(0, 74, 173, 0.05) 0%, rgba(20, 20, 32, 0) 100%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '4px 0 30px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 20px',
          minHeight: '42px',
          minWidth: '120px',
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
          transform: 'none',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 74, 173, 0.25)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #004aad 0%, #3373c4 100%)',
          color: '#FFFFFF',
          transform: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #3373c4 0%, #004aad 100%)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: 'rgba(0, 74, 173, 0.5)',
          color: '#3373c4',
          '&:hover': {
            borderColor: '#004aad',
            backgroundColor: 'rgba(0, 74, 173, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A25',
          backgroundImage: 'none',
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1A1A25',
            borderRadius: 12,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 74, 173, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#004aad',
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#272733',
          color: '#E0E0E5',
          fontSize: '0.875rem',
          borderRadius: 8,
          padding: '8px 12px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 74, 173, 0.7)',
          '&.Mui-selected': {
            color: '#3373c4',
          },
          '&:hover': {
            color: '#3373c4',
            backgroundColor: 'rgba(0, 74, 173, 0.1)',
          },
        },
      },
    },
  },
});

const AppContent = () => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [
    mode,
  ]);
  const [editorStage, setEditorStage] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const imageGeneratorRef = useRef(null);
  const navbarRef = useRef(null);
  const [session, setSession] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  // Removed isAdmin state
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [activeTutorials, setActiveTutorials] = useState({});
  const [featureHighlights, setFeatureHighlights] = useState({});

  // Add refs for workflow sections
  const workflowRef = useRef(null);
  const modeRef = useRef(null);
  const styleRef = useRef(null);
  const uploadRef = useRef(null);
  const promptRef = useRef(null);
  const generateRef = useRef(null);

  const getPageTitle = () => {
    switch (activeTab) {
      case 0:
        return 'Sanalyapi - Yeni Görsel Oluştur';
      case 1:
        return 'Sanalyapi - Gösterge Paneli';
      case 2:
        return 'Sanalyapi - Projelerim';
      case 3:
        return 'Sanalyapi - Galeri';
      case 4:
        return 'Sanalyapi - Yönetici Arayüzü';
      case 5:
        return 'Sanalyapi - Poz Konfigürasyonu';
      default:
        return 'Sanalyapi - Mimari Yapay Zeka Dönüşüm Araçları';
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case 0:
        return "Sanalyapi'nin AI destekli araçlarıyla yeni mimari çıktılar oluşturun.";
      case 1:
        return 'Kişisel gösterge paneliniz, başarılarınız ve istatistikleriniz.';
      case 2:
        return 'Sanalyapi ile mimari görselleştirme projelerinizi görüntüleyin ve yönetin.';
      case 3:
        return 'Sanalyapi araçlarıyla oluşturulmuş mimari görsellerin vitrinini keşfedin.';
      case 4:
        return 'Kullanıcı erişimini ve sistem ayarlarını yönetmek için yönetici arayüzü.';
      case 5:
        return 'Mimari görselleştirme için poz ve perspektif ayarlarını yapılandırın.';
      default:
        return 'Sanalyapi: AI destekli profesyonel mimari görselleştirme ve düzenleme araçları.';
    }
  };

  // Removed checkAdminStatus effect

  // Check if onboarding should be shown
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('id', user.id)
            .single();
            
          if (profile && profile.onboarding_completed === false) {
            setShowOnboarding(true);
          } else {
            setOnboardingCompleted(true);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };
    
    checkOnboardingStatus();
  }, [session]);

  // Handle ResizeObserver errors
  useEffect(() => {
    const resizeObserverError = (error) => {
      if (error.message.includes('ResizeObserver')) {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.style.display = 'none';
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.style.display = 'none';
        }
        error.stopImmediatePropagation();
      }
    };

    window.addEventListener('error', resizeObserverError);
    return () => window.removeEventListener('error', resizeObserverError);
  }, []);

  useEffect(() => {
    let timeoutId;
    let authSubscription;

    const initAuth = async () => {
      // Get initial session only once
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (!error) {
        setSession(session);
      }

      // Set up auth listener with debouncing
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setSession(session);
        }, 2000); // Debounce for 2 seconds
      });

      authSubscription = subscription;
    };

    initAuth();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (authSubscription) authSubscription.unsubscribe();
    };
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAnchorEl(null);
    setActiveTab(0);
  };

  const [warningOpen, setWarningOpen] = useState(false);

  const handleTabChange = (event, newValue) => {
    if (loading) {
      setWarningOpen(true);
      return;
    }

    // Clear the active tab state
    setActiveTab(newValue);

    // Handle navigation based on the new value
    if (newValue === 5) {
      // Workflows tab
      navigate('/workflows');
    } else if (newValue === 0) {
      // Canvas Editor tab
      navigate('/canvas-editor');
    } else if (newValue === 2) {
      // Dashboard tab
      navigate('/dashboard');
    }
  };

  const handleWarningClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setWarningOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Allow spacebar in text input fields
      if (
        e.target.tagName === 'TEXTAREA' ||
        e.target.tagName === 'INPUT' ||
        e.target.isContentEditable
      ) {
        return;
      }

      if (e.code === 'Space' || e.keyCode === 32) {
        e.preventDefault(); // Prevent default scrolling when Spacebar is pressed
      }
    };

    // Listen for custom event to open profile dialog
    const handleOpenProfileDialog = (event) => {
      if (session) {
        setEditProfileOpen(true);
        // Set the tab index if provided
        if (event.detail && typeof event.detail.tabIndex !== 'undefined') {
          setTimeout(() => {
            const tabValue = event.detail.tabIndex;
            const profileTabEvent = new CustomEvent('setProfileTab', { 
              detail: { tabIndex: tabValue } 
            });
            window.dispatchEvent(profileTabEvent);
          }, 100);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    window.addEventListener('openProfileDialog', handleOpenProfileDialog);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('openProfileDialog', handleOpenProfileDialog);
    };
  }, [session]);

  // Get the selected workflow ID from the URL search params
  useEffect(() => {
    if (location.pathname === '/canvas-editor') {
      const searchParams = new URLSearchParams(location.search);
      const workflowId = searchParams.get('workflow');
      setSelectedWorkflowId(workflowId);
    }
  }, [location]);

  // Handle onboarding completion
  const handleOnboardingComplete = useCallback((profileData) => {
    setShowOnboarding(false);
    setOnboardingCompleted(true);
    setUserProfile(prev => ({
      ...prev,
      ...profileData,
      onboarding_completed: true
    }));
    
    // Initialize tutorials based on experience level
    if (profileData.preferences?.tutorials) {
      const newTutorials = {};
      
      if (profileData.experienceLevel === 'beginner') {
        newTutorials.workflowSelection = true;
        newTutorials.styleSelection = true;
        newTutorials.imageUpload = true;
        newTutorials.promptWriting = true;
      } else if (profileData.experienceLevel === 'intermediate') {
        newTutorials.workflowSelection = true;
        newTutorials.promptWriting = true;
      }
      
      setActiveTutorials(newTutorials);
    }
    
    console.log('Onboarding completed with profile:', profileData);
  }, []);
  
  // Handle dismissing a tutorial
  const handleDismissTutorial = useCallback((tutorialId) => {
    setActiveTutorials(prev => ({
      ...prev,
      [tutorialId]: false
    }));
    
    // Optionally save dismissed tutorials to user profile
    const saveDismissedTutorial = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({
              dismissed_tutorials: { ...userProfile?.dismissed_tutorials, [tutorialId]: true }
            })
            .eq('id', user.id);
        }
      } catch (error) {
        console.error('Error saving dismissed tutorial:', error);
      }
    };
    
    saveDismissedTutorial();
  }, [userProfile]);

  const handleAuthClose = useCallback(() => {
    setAuthOpen(false);
  }, [setAuthOpen]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SWRConfig
        value={{
          fetcher: (...args) => fetch(...args).then((res) => res.json()),
          revalidateOnFocus: false,
          shouldRetryOnError: false,
        }}
      >
        <>
          {/* Onboarding Dialog */}
          <Onboarding 
            open={showOnboarding && session} 
            onClose={() => setShowOnboarding(false)}
            onComplete={handleOnboardingComplete}
          />
          <Helmet>
            <title>{getPageTitle()}</title>
            <meta name="description" content={getPageDescription()} />
          </Helmet>
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(26, 26, 38, 0.95)'
                : '#fdfdfd',
              borderBottom: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 74, 173, 0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 1600,
              position: 'sticky',
              top: 0,
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              '& .MuiToolbar-root': {
                minHeight: { xs: '64px', sm: '70px' },
                height: { xs: '64px', sm: '70px' },
                padding: { xs: '0 16px', sm: '0 24px', md: '0 32px' },
              },
            }}
          >
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <IconButton
                  color="inherit"
                  aria-label="toggle drawer"
                  onClick={() => setOpen(!open)}
                  sx={{
                    mr: 2,
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {open ? <ChevronLeftIcon /> : <MenuIcon />}
                </IconButton>
                <Box
                  sx={{
                    display: {
                      xs:
                        window.location.pathname === '/canvas-editor'
                          ? 'none'
                          : 'flex',
                      sm: 'flex',
                    },
                  }}
                >
                  <Logo onClick={() => navigate('/')} />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  ml: 'auto', // Push to right side of toolbar
                }}
              >
                <IconButton
                  onClick={useThemeMode().toggleTheme}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#fdfdfd' : '#004aad',
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 74, 173, 0.15)'
                      : 'rgba(203, 241, 153, 0.3)',
                    borderRadius: '8px',
                    padding: '8px',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(0, 74, 173, 0.3)'
                      : '1px solid rgba(162, 195, 164, 0.5)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(0, 74, 173, 0.25)'
                        : 'rgba(203, 241, 153, 0.5)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  {theme.palette.mode === 'dark' ? (
                    <Brightness7Icon fontSize="small" />
                  ) : (
                    <Brightness4Icon fontSize="small" />
                  )}
                </IconButton>
                {window.location.pathname === '/canvas-editor' && (
                  <Box
                    sx={{
                      display: 'block',
                      position: 'relative',
                      zIndex: 1200,
                      width: '100%',
                      maxWidth: { xs: '100%', md: '800px' },
                    }}
                  >
                    <WorkflowTracker
                      currentStage={editorStage?.currentStage || 'workflow'}
                      completedStages={editorStage?.completedStages || []}
                      workflowId={selectedWorkflowId}
                      onStageClick={(stageId) => {
                        const refs = {
                          workflow: workflowRef,
                          mode: modeRef,
                          style: styleRef,
                          upload: uploadRef,
                          prompt: promptRef,
                          generate: generateRef,
                        };
                        const ref = refs[stageId];
                        if (ref?.current) {
                          ref.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        }
                      }}
                    />
                  </Box>
                )}
                {!session && (
                  <Button
                    onClick={() => navigate('/login')}
                    variant="contained"
                    sx={{
                      backgroundColor: '#004aad',
                      color: '#fdfdfd',
                      fontWeight: 600,
                      padding: '8px 16px',
                      borderRadius: '8px',
                      textTransform: 'none',
                      boxShadow: '0 2px 8px rgba(0, 74, 173, 0.3)',
                      '&:hover': {
                        backgroundColor: '#003580',
                        boxShadow: '0 4px 12px rgba(0, 74, 173, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Giriş Yap
                  </Button>
                )}
              </Box>
            </Toolbar>
          </AppBar>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <Sidebar
              open={open}
              setOpen={setOpen}
              handleDrawerClose={() => setOpen(false)}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              // Removed isAdmin prop
              session={session}
              onEditProfile={() => setEditProfileOpen(true)}
              setAuthOpen={setAuthOpen}
            />
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: 4,
                mt: 0,
                marginLeft: { xs: 0, sm: open ? '280px' : '0px' },
                width: {
                  xs: '100%',
                  sm: open ? `calc(100% - 280px)` : '100%',
                },
                maxWidth: '100vw',
                transition: (theme) =>
                  theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '100vh',
                paddingBottom: '24px',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                background: 'none',
                transition:
                  'margin-left 0.3s ease-in-out, width 0.3s ease-in-out',
                paddingLeft: {
                  xs: '0px',
                  sm: '0px',
                  md: '0px',
                  lg: '0px',
                  xl: '0px',
                },
                paddingRight: {
                  xs: '0px',
                  sm: '0px',
                  md: '0px',
                  lg: '0px',
                  xl: '0px',
                },
                backgroundColor: theme.palette.mode === 'dark' ? '#0A0A0F' : '#FFFFFF',
                '& > *': {
                  maxWidth: '100%',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                }
            }}>
                {window.location.pathname === '/video-generator' ? (
                  <Box sx={{
                    minHeight: 'calc(100vh - 84px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: '24px',
                    paddingBottom: '24px',
                    WebkitOverflowScrolling: 'touch',
                    position: 'relative'
                  }}>
                    <Suspense fallback={
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        transform: 'translateZ(0)'
                      }}>
                        <CircularProgress />
                      </Box>
                    }>
                      <ErrorBoundary fallback={<div>Video Oluşturucu yüklenirken hata oluştu</div>}>
                        <VideoGenerator session={session} />
                      </ErrorBoundary>
                    </Suspense>
                  </Box>
                ) : window.location.pathname === '/document-generator' ? (
                  <Box sx={{
                    minHeight: 'calc(100vh - 84px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: '24px',
                    paddingBottom: '24px',
                    WebkitOverflowScrolling: 'touch',
                    position: 'relative'
                  }}>
                    <Suspense fallback={
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        transform: 'translateZ(0)'
                      }}>
                        <CircularProgress />
                      </Box>
                    }>
                      <ErrorBoundary fallback={<div>Belge Oluşturucu yüklenirken hata oluştu</div>}>
                        <DocumentGenerator />
                      </ErrorBoundary>
                    </Suspense>
                  </Box>
                ) : (window.location.pathname === '/canvas-editor') ? (
                  <Box sx={{
                    minHeight: 'calc(100vh - 84px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingTop: '24px',
                    paddingBottom: '24px',
                    WebkitOverflowScrolling: 'touch',
                    position: 'relative',
                    background: 'none',
                    '& .MuiModal-root': {
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    },
                    '@media (min-width: 600px)': {
                      '&::-webkit-scrollbar': {
                        display: 'none'
                      },
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }
                  }}>
                    <Suspense fallback={
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        background: 'none',
                        transform: 'translateZ(0)'
                      }}>
                        <CircularProgress />
                      </Box>
                    }>
                      <ErrorBoundary fallback={<div>Canvas Editörü yüklenirken hata oluştu</div>}>
                        <Box sx={{ display: 'flex' }}>
                          <CanvasEditor onStageChange={setEditorStage} />
                        </Box>
                      </ErrorBoundary>
                    </Suspense>
                  </Box>
                ) : window.location.pathname === '/workflows' ? (
                  <WorkflowDirectory />
                ) : window.location.pathname === '/dashboard' ? (
                  <UserDashboard />
                ) : window.location.pathname === '/gallery' ? (
                  <Gallery />
                ) : window.location.pathname === '/properties' ? (
                  <PropertyGallery />
                ) : window.location.pathname.startsWith('/property/') ? (
                  <PropertyDetail />
                ) : window.location.pathname === '/shared-links' ? (
                  <SharedLinks />
                ) : window.location.pathname === '/chat' ? (
                  <Box sx={{ 
                    width: '100%', 
                    maxWidth: '100vw', 
                    height: 'calc(100vh - 84px)',
                    overflow: 'hidden',
                    margin: 0,
                    padding: 0
                  }}>
                    <ChatTab />
                  </Box>
                ) : window.location.pathname.startsWith('/editor/') ? (
                  <Box sx={{ 
                    width: '100%', 
                    maxWidth: '100vw', 
                    height: 'calc(100vh - 84px)',
                    overflow: 'hidden',
                    margin: 0,
                    padding: 0
                  }}>
                    <ImageEditor />
                  </Box>
                ) : null /* Removed AdminInterface rendering */}
              </Box>
            </Box>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={warningOpen}
            autoHideDuration={6000}
            onClose={handleWarningClose}
            message="Bir görsel oluşturulurken sekmeler değiştirilemez"
          />
          <Footer />
          {session && (
            <EditProfile
              open={editProfileOpen}
              onClose={() => setEditProfileOpen(false)}
              user={session.user}
              onUpdate={(newSession, newProfile) => {
                setSession(newSession);
              }}
            />
          )}
        </>
        </SWRConfig>
    </ThemeProvider>
  );
}

// Auth guard component
const RequireAuth = ({ children }) => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  // Set data attribute on body for subscription page
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.pathname === '/subscription') {
        document.body.setAttribute('data-page', 'subscription');
      } else {
        document.body.removeAttribute('data-page');
      }
    };

    // Initial check
    handleRouteChange();

    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      document.body.removeAttribute('data-page');
    };
  }, []);

  return (
    <CustomThemeProvider>
        <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/workflows" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password-confirm" element={<ResetPasswordConfirmPage />} />
          <Route
            path="/subscription"
            element={
              <Suspense fallback={<CircularProgress />}>
                <Box sx={{ width: '100vw', maxWidth: '100vw', overflowX: 'hidden' }}>
                  <SubscriptionWrapper />
                </Box>
              </Suspense>
            }
          />
          <Route path="/canvas-editor" element={<AppContent />} />
          <Route path="/video-generator" element={<AppContent />} />
          <Route path="/document-generator" element={<AppContent />} />
          <Route path="/workflows" element={<AppContent />} />
          <Route 
            path="/dashboard" 
            element={
              <Suspense fallback={<CircularProgress />}>
                <RequireAuth>
                  <AppContent />
                </RequireAuth>
              </Suspense>
            } 
          />
          <Route path="/gallery" element={<AppContent />} />
          <Route path="/properties" element={<AppContent />} />
          <Route path="/property/:propertyId" element={<AppContent />} />
          <Route path="/shared-links" element={<AppContent />} />
          <Route path="/chat" element={<AppContent />} />
          <Route path="/editor/:imageId" element={<AppContent />} />
          {/* Removed /admin route */}
          <Route path="/share/:shareId" element={<SharedItemViewer />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </CustomThemeProvider>
  );
}

export default App;
